/**
 * F3 -- one place that decides how a date looks.
 *
 * Twenty-one surfaces called `toLocaleString()` with no locale, so the
 * format followed whatever the browser happened to be set to: a Saudi
 * bank's screens showed "8/30/2026", which reads as 8 March to most of
 * the people who will use this and as 30 August to the rest. A date a
 * reader can misread by five months is worse than an ISO string.
 *
 * en-GB when the interface is English, ar-SA when it is Arabic --
 * following the language the reader chose rather than the machine they
 * happen to be on.
 */

/**
 * G -- the calendar and the digits are stated, not inherited.
 *
 * `ar-SA` alone lets the reader's browser decide both. Chrome resolves
 * it to the Umm al-Qura calendar, so a CAP due date rendered as
 * 18/3/1448 on the walkthrough machine while the same date on the
 * signed pack, the PDF and the contract it answers to was 30/8/2026.
 * Node's ICU here resolves the same locale to Gregorian, which is why
 * no test saw it: the output depended on which runtime drew it.
 *
 * Two calendars on one screen is a date somebody acts on wrongly, and
 * the one that governs is the one in the evidence -- Gregorian, because
 * that is what every artefact this product signs already carries.
 * Offering Hijri is a reasonable thing for a Saudi bank to want; it has
 * to be a choice somebody makes, not a property of a browser build.
 *
 * `nu-latn` for the same reason. Money is formatted `en-SA` and always
 * shows Latin digits, so leaving dates to default gave an Arabic page
 * reading "SAR 2,400,000" beside "١٨‏/٣‏/١٤٤٨". Arabic-Indic numerals
 * are right for Arabic typography, but not for half of it.
 */
const LOCALE_BY_LANGUAGE: Record<string, string> = {
  en: "en-GB",
  ar: "ar-SA-u-ca-gregory-nu-latn",
};

export function localeFor(language: string | undefined): string {
  const base = (language ?? "en").split("-")[0] ?? "en";
  return LOCALE_BY_LANGUAGE[base] ?? "en-GB";
}

/** A day. "30 Aug 2026". */
export function formatDate(
  value: string | Date | null | undefined,
  language?: string,
): string {
  const d = toDate(value);
  if (!d) return "";
  return d.toLocaleDateString(localeFor(language), {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** A moment. "30 Aug 2026, 14:05" -- no seconds, and never
 *  microseconds: nobody reading a screen needs them, and they push the
 *  part that matters off the end of the line. */
export function formatDateTime(
  value: string | Date | null | undefined,
  language?: string,
): string {
  const d = toDate(value);
  if (!d) return "";
  return d.toLocaleString(localeFor(language), {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    // D4 -- Arabic locales default to a 12-hour clock, so the same
    // moment read "11:34 م" in Arabic and "23:34" in English. An
    // operations screen showing two different clocks depending on the
    // reader's language is a defect in the record, not a preference:
    // an incident timeline has to mean the same thing to both people
    // looking at it.
    hourCycle: "h23",
  });
}

/** A clock time. "14:05" -- for a moment close enough that the day is
 *  understood, like the next sample of a metric refreshed each minute. */
export function formatTime(
  value: string | Date | null | undefined,
  language?: string,
): string {
  const d = toDate(value);
  if (!d) return "";
  return d.toLocaleTimeString(localeFor(language), {
    hour: "2-digit",
    minute: "2-digit",
    // One clock, both languages -- see formatDateTime.
    hourCycle: "h23",
  });
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  // An unparseable string is a bug upstream; rendering "Invalid Date"
  // in the middle of a board pack is not the way to report it.
  return Number.isNaN(d.getTime()) ? null : d;
}
